import * as React from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import {
  Lock, Layers, UserPlus, UserMinus, MessageSquare, Eye, Check,
  ChevronDown, ChevronRight, ChevronsDown, ChevronsUp,
  Search, X, CheckCircle2, CheckSquare2,
  Phone, Mail, MapPin, Minimize2, Maximize2, UserCog, Pause, Play,
  MoreVertical, Zap, ZoomIn, Loader2,
} from "lucide-react";
import {
  findValueById,
  changeTasks,
  prepareGetTasks,
  getTemplateForm,
  find,
  preparTaskForm,
  saveCommentary,
  getUserName,
  searchPhone,
  searchMail,
  isAdminUser,
  canReassignTasks,
  getReassignCandidates,
  reassignTask,
  suspendTask,
  resumeTask,
  getTaskDetails,
} from "../../utils/Functions";
import Loading from "../customdesign/Loading";
import DialogExecuteTask from "../dialog/DialogExecuteTask";

const MAX_COL = 12;

/* ─────────────────────────────────────────────────────────────
   CompTreeGrid  –  tabla de tareas sin ninguna dependencia de
   Syncfusion (excepto DialogExecuteTask que ya tiene su propio
   Syncfusion DialogComponent y no es el cuello de botella).
───────────────────────────────────────────────────────────────*/

/* ─── Portal tooltip para la oficina (evita clipping de celdas de tabla) ─── */
function OfficeTooltip({ triggerRef, officecode, officename, wfpflowid }) {
  const [pos, setPos] = React.useState(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    const show = () => {
      const r = el.getBoundingClientRect();
      setPos({ top: r.top + r.height / 2, left: r.right + 10 });
      setVisible(true);
    };
    const hide = () => setVisible(false);
    el.addEventListener("mouseenter", show);
    el.addEventListener("focus", show);
    el.addEventListener("mouseleave", hide);
    el.addEventListener("blur", hide);
    return () => {
      el.removeEventListener("mouseenter", show);
      el.removeEventListener("focus", show);
      el.removeEventListener("mouseleave", hide);
      el.removeEventListener("blur", hide);
    };
  }, [triggerRef]);

  if (!pos) return null;
  return ReactDOM.createPortal(
    <div
      className="xft-num-tip-pop"
      role="tooltip"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        transform: "translateY(-50%)",
        opacity: visible ? 1 : 0,
        pointerEvents: "none",
        transition: "opacity 0.15s ease",
        zIndex: 99999,
      }}
    >
      <span className="xft-num-tip-label">Sección</span>
      <span className="xft-num-tip-code">{officecode}</span>
      {officename && <span className="xft-num-tip-name">{officename}</span>}
      {wfpflowid && wfpflowid > 0 && <span className="xft-num-tip-flow">Flujo #{wfpflowid}</span>}
    </div>,
    document.body
  );
}

function OfficeNumCell({ num, officecode, officename, wfpflowid }) {
  const ref = React.useRef(null);
  return (
    <span className="xft-num-tip" ref={ref} tabIndex={0}>
      {num}
      <OfficeTooltip triggerRef={ref} officecode={officecode} officename={officename} wfpflowid={wfpflowid} />
    </span>
  );
}

/* ─── Menú "más acciones" (⋮) — portal, se posiciona junto al botón trigger.
   Patrón usado por Jira/Linear/Asana: 1-2 CTA visibles + resto en overflow,
   para no llenar la fila de botones (se veía desordenado con 5 botones). ─── */
function MoreActionsMenu({ items, triggerClassName, triggerTitle }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const visibleItems = (items || []).filter((it) => !it.hidden);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({
        top: r.bottom + 6,
        left: Math.min(r.left, window.innerWidth - 210),
        triggerTop: r.top,
        flipped: false,
      });
    }
    setOpen((o) => !o);
  };

  // Si el trigger está cerca del final de la tabla (última fila), el menú
  // abierto hacia abajo se sale del viewport y queda cortado — se mide una
  // vez ya montado en el portal y, si no entra, se voltea hacia arriba del
  // botón en vez de hacia abajo.
  React.useLayoutEffect(() => {
    if (!open || !pos || pos.flipped || !menuRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    if (menuRect.bottom > window.innerHeight) {
      const flippedTop = Math.max(8, pos.triggerTop - menuRect.height - 6);
      setPos((p) => ({ ...p, top: flippedTop, flipped: true }));
    }
  }, [open, pos]);

  React.useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDocDown);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  if (visibleItems.length === 0) return null;

  return (
    <>
      <button type="button" ref={btnRef} className={triggerClassName || "xft-btn xft-btn-ghost xft-btn-more"}
        title={triggerTitle || "Más acciones"} onClick={toggle}>
        <MoreVertical size={15} />
      </button>
      {open && pos && ReactDOM.createPortal(
        <div ref={menuRef} className="xft-more-menu" style={{ top: pos.top, left: pos.left }}>
          {visibleItems.map((it, i) => (
            <button key={i} type="button" className="xft-more-menu-item" disabled={it.disabled} title={it.title}
              onClick={() => { setOpen(false); it.onClick(); }}>
              {it.icon}
              <span>{it.label}</span>
              {it.badge > 0 && <span className="xft-comment-count-badge">{it.badge}</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

/* Serializa cualquier valor a string seguro para JSX.
   Previene el error "Objects are not valid as a React child"
   cuando MySQL devuelve objetos Date en columnas datetime. */
function safeCell(val) {
  if (val === null || val === undefined) return "";
  if (val instanceof Date) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(val.getDate())}/${pad(val.getMonth() + 1)}/${val.getFullYear()}`;
  }
  if (typeof val === "object") return JSON.stringify(val);
  return val;
}

function safeTransferStore(val) {
  if (Array.isArray(val)) return val.filter(Boolean).join(" / ");
  return safeCell(val);
}

class CompTreeGrid extends React.Component {
  constructor(props) {
    super(props);
    this.commentRef = React.createRef();
    // Token de carrera: cada setAction() toma un nro. Si dos acciones lentas se
    // superponen (ej: RESOLVE tarda, usuario abre REASSIGN antes que resuelva),
    // la callback vieja se descarta en vez de pisar el diálogo ya abierto
    // ("se queda colgado" = el overlay de la acción vieja tapaba el nuevo popup).
    this.actionSeq = 0;
    this.isAdmin = isAdminUser();
    this.canReassign = canReassignTasks(); // ADMIN o SUPERVISOR
    const { defConfig, defLang } = props;

    this.state = {
      // data
      data: [],
      vars: [],
      header: [],
      // task form dialog
      taskHeader: "",
      taskName: "",
      formDataTask: [],
      flowData: [],
      wfptaskid: -1,
      wfpflowid: -1,
      officecode: "",
      id: null,
      commentary: "",
      datatitle: "",
      datasubtitle: "",
      datapostload: null,
      isParent: false,
      readOnly: false,
      formDialogStatus: false,
      formDialogs: [],
      // ui
      loading: false,
      deepSearching: false, // búsqueda profunda por variable (endpoint by-var) en vuelo
      isMobile: this.getIsMobileView(),
      wHeight: window.innerHeight - 361,
      // table
      expandedIds: new Set(),
      keySearch: "",
      // readOnly hasta el primer foco: evita que Chrome autocomplete el
      // buscador con credenciales guardadas (email) y filtre la tabla a 0.
      searchReadOnly: true,
      selectedTasks: [],
      activeTabIndex: 0,
      selAll: false,
      notGroupable: false,
      selectMode: false,   // false | 'group' | 'take'
      skeletonLoading: false,
      toolbarOptions: [],
      compactMode: localStorage.getItem("xftc_compact") === "1",
      // simple modals (reemplazo de DialogComponent de Syncfusion)
      showProgressDialog: false,
      showCommentDialog: false,
      commentaryHistory: "",
      // reasignar (delegar) tarea
      showReassignDialog: false,
      reassignLoading: false,
      reassignCandidates: [],
      reassignTargetId: "",
      reassignBusy: false,
      // suspender tarea: pide motivo obligatorio antes de suspender
      showSuspendDialog: false,
      suspendReason: "",
      suspendBusy: false,
      // forzar tarea (ADMIN): ejecuta sin pasar por el formulario
      showForceDialog: false,
      forceLoading: false,
      forceOptions: [],
      forceComment: "",
      forceTaskName: "",
      progressValue: 20,
      // actions
      taskActions: [{
        click: () => this.formDialogClose(),
        buttonModel: { content: "CLOSE", isSecondary: true },
      }],
      actions: [
        { text: defConfig[defLang].executelabel, value: "RESOLVE" },
        { text: defConfig[defLang].take,         value: "TAKE"    },
        ...(!process.env.REACT_APP_HIDE_ADDCOMMENTARY ? [
          { text: defConfig[defLang].addcomment, value: "COMMENT" },
        ] : []),
      ],
      actions2: [
        { text: defConfig[defLang].executelabel,  value: "RESOLVE" },
        { text: defConfig[defLang].take,          value: "TAKE"    },
        { text: defConfig[defLang].ungrouplabel,  value: "UNGROUP" },
        ...(!process.env.REACT_APP_HIDE_ADDCOMMENTARY ? [
          { text: defConfig[defLang].addcomment,  value: "COMMENT" },
        ] : []),
      ],
    };
  }

  /* ── helpers ─────────────────────────────────────────────── */
  headerText = [
    { id: "assigned",   text: this.props.defConfig[this.props.defLang].assigned   },
    { id: "unassigned", text: this.props.defConfig[this.props.defLang].unassigned },
    { id: "all",        text: this.props.defConfig[this.props.defLang].all        },
  ];

  getIsMobileView = () => window.innerWidth <= 768;

  updateMobileView = () => {
    const isMobile = this.getIsMobileView();
    if (isMobile !== this.state.isMobile) this.setState({ isMobile });
  };

  resolveFieldValue(row, i) {
    const val = row["field" + i];
    if (Array.isArray(val)) return val[0] ? val[1] : "-";
    return safeCell(val);
  }

  /* ── filter helpers (lógica original preservada) ─────────── */
  _enrichCoords(data) {
    let lat, lng;
    this.state.header.map((b, index) => {
      if (b.VARNAME === "latitude")  lat = index;
      if (b.VARNAME === "longitude") lng = index;
    });
    return data.map((d) => {
      const out = { ...d };
      if (lat >= 0 && lng >= 0) {
        const la = parseFloat(d["field" + lat]);
        const lo = parseFloat(d["field" + lng]);
        if (!isNaN(la) && !isNaN(lo) && la !== 0 && lo !== 0) {
          out.haveCoords = { latitude: la, longitude: lo };
        }
      }
      out.havePhone = searchPhone(d);
      out.haveMail  = searchMail(d);
      return out;
    });
  }

  _searchFilter(rows) {
    const q = this.state.keySearch;
    if (q.length < 3) return rows;
    return rows.filter((d) => {
      for (let i = 0; i < MAX_COL; i++) {
        if (d["field" + i] && String(d["field" + i]).toLowerCase().indexOf(q) >= 0)
          return true;
      }
      return false;
    });
  }

  getAssigned() {
    return this._searchFilter(
      this._enrichCoords(this.state.data.filter((d) => d.assigned))
    );
  }

  getUnassigned() {
    return this._searchFilter(
      this._enrichCoords(this.state.data.filter((d) => !d.assigned && !d.locked))
    );
  }

  getAll() {
    return this._searchFilter(this._enrichCoords(this.state.data));
  }

  /* ── lifecycle ───────────────────────────────────────────── */
  componentDidMount() {
    this.mounted = true;
    window.addEventListener("resize", this.updateMobileView);
    document.addEventListener("change", this.checkClick);

    const { dataSource: data, vars, header, defConfig, defLang, tasktypeId, noToolBar } = this.props;

    const toolbarOptions = [
      {
        text: defConfig[defLang].refreshlabel,
        prefixIcon: "e-icons e-refresh",
        id: "refresh",
      },
    ];

    if (!noToolBar) {
      if (data.length > 0 && data[0].isassignable === "YES") {
        toolbarOptions.push({ text: defConfig[defLang].take, prefixIcon: "e-icons e-pan", id: "take" });
      }
      if (tasktypeId === 53) {
        toolbarOptions.push({ prefixIcon: "e-icons e-group-2", id: "executegroup", text: defConfig[defLang].executetaskslabel });
      } else if (data.length > 0) {
        if (data[0].isgroupable === "YES") {
          toolbarOptions.push({ prefixIcon: "e-icons e-group-2", id: "group", text: defConfig[defLang].grouptasks });
        } else {
          this.setState({ notGroupable: true });
        }
      }
    }

    this.setState({
      data,
      vars,
      header,
      toolbarOptions,
      isMobile: this.getIsMobileView(),
      wHeight: window.innerHeight - 361,
    });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.dataSource !== this.props.dataSource ||
      prevProps.vars !== this.props.vars ||
      prevProps.header !== this.props.header
    ) {
      this.setState({
        data: this.props.dataSource || [],
        vars: this.props.vars || [],
        header: this.props.header || [],
        expandedIds: new Set(),
        selectedTasks: [],
        selAll: false,
        selectMode: false,
        skeletonLoading: false,
        loading: false,
      });
    }
  }

  // Activa el skeleton localmente sin necesidad de desmontar el componente.
  // Se llama desde el padre (via ref) antes de despachar una carga paginada.
  startSkeleton = () => {
    this.setState({ skeletonLoading: true });
  };

  /* ── búsqueda profunda por variable (endpoint get-active-tasks-by-var) ──
     El input filtra la lista actual en vivo (keySearch). La lupita dispara
     la búsqueda profunda en el backend pasando el texto como VARVALUE; el
     padre (ListTasks) reemplaza la bandeja con los resultados y muestra el
     chip de salida. Se exige >= 2 caracteres (mismo umbral que el filtro). */
  triggerDeepSearch = () => {
    const q = (this.state.keySearch || "").trim();
    if (q.length < 2) return;
    if (typeof this.props.onDeepSearch !== "function") return;
    this.setState({ deepSearching: true });
    Promise.resolve(this.props.onDeepSearch(q))
      .catch(() => {})
      .finally(() => {
        if (this.mounted) this.setState({ deepSearching: false });
      });
  };

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener("resize", this.updateMobileView);
    document.removeEventListener("change", this.checkClick);
  }

  /* ── checkbox para selección múltiple ───────────────────── */
  checkClick = (event) => {
    const elem = event.target;
    if (elem.className && elem.id && elem.type === "checkbox") {
      const parts = elem.id.split("_");
      const id = parts[2];
      if (!id) return;
      const selectedTasks = [...this.state.selectedTasks];
      const idx = selectedTasks.indexOf(id);
      if (idx < 0) selectedTasks.push(id);
      else selectedTasks.splice(idx, 1);
      this.setState({ selectedTasks });
    }
  };

  /* ── toolbar desktop ─────────────────────────────────────── */
  contextToolbarMenuTaskClick = async (args) => {
    if (!args?.item?.id) return;
    const { id } = args.item;

    if (id === "refresh") {
      this.refreshData(args);
      return;
    }

    // Primera pulsación en group/take → activa modo selección
    if ((id === "group" || id === "take") && !this.state.selectMode) {
      this.setState({ selectMode: id === "group" ? "group" : "take", selectedTasks: [], selAll: false });
      return;
    }

    const selectedrecords = this.state.data.filter((d) =>
      this.state.selectedTasks.includes(String(d.id))
    );

    if (id === "executegroup") {
      const fortemporalyIds = selectedrecords.map((r) => r.id);
      await this.setAction({ item: { value: "RESOLVE" } }, fortemporalyIds);
    } else if (id === "group") {
      const unlocked = selectedrecords.filter((d) => !d.locked);
      if (!unlocked.length) {
        this.props.setMessage({ status: false, message: this.props.defConfig[this.props.defLang].notaskselected });
        return;
      }
      this.setState({ loading: true, selectMode: false });
      const resp = await changeTasks(unlocked, "group");
      this.props.setMessage(resp);
      if (resp.status) this.refreshDataLocal(args);
      else this.setState({ loading: false });
    } else if (id === "take") {
      if (!selectedrecords.length) {
        this.props.setMessage({ status: false, message: this.props.defConfig[this.props.defLang].notaskselected });
        return;
      }
      this.setState({ loading: true, selectMode: false });
      const resp = await changeTasks(selectedrecords, "take");
      this.props.setMessage(resp);
      if (resp.status) this.refreshDataLocal(args);
      else this.setState({ loading: false });
    }
  };

  /* ── selección masiva desktop ────────────────────────────── */
  confirmSelection = async () => {
    this.contextToolbarMenuTaskClick({ item: { id: this.state.selectMode } });
  };

  cancelSelection = () => {
    this.setState({ selectMode: false, selectedTasks: [], selAll: false });
    // desmarcar checkboxes visualmente
    document.querySelectorAll(".xft-desktop-check").forEach((el) => { el.checked = false; });
  };

  toggleSelectAll = () => {
    const { data, selAll, selectMode } = this.state;
    if (!selAll) {
      const ids = data.filter((d) => !d.locked && !Array.isArray(d.id)).map((d) => String(d.id));
      this.setState({ selectedTasks: ids, selAll: true });
      setTimeout(() => {
        ids.forEach((id) => {
          const el = document.getElementById("dTASK_CHECK_" + id);
          if (el) el.checked = true;
        });
      }, 10);
    } else {
      this.setState({ selectedTasks: [], selAll: false });
      document.querySelectorAll(".xft-desktop-check").forEach((el) => { el.checked = false; });
    }
  };

  /* ── mobile toolbar (select-all / group) ────────────────── */
  contextMenuClick = async (args) => {
    this.setState({ formDialogStatus: false });
    if (!args?.item?.id) return;
    const { id } = args.item;
    const { activeTabIndex } = this.state;

    if (id === "selectall") {
      const tasks = activeTabIndex === 0 ? this.getAssigned()
                  : activeTabIndex === 1 ? this.getUnassigned()
                  : this.getAll();
      if (!this.state.selAll) {
        const ids = tasks.filter((t) => !t.locked && !Array.isArray(t.id)).map((t) => t.id.toString());
        this.setState({ selectedTasks: ids, selAll: true });
        setTimeout(() => {
          ids.forEach((id) => {
            const el = document.getElementById(`${activeTabIndex}TASK_CHECK_${id}`);
            if (el) el.checked = true;
          });
        }, 10);
      } else {
        this.setState({ selectedTasks: [], selAll: false });
        setTimeout(() => {
          tasks.forEach((t) => {
            const el = document.getElementById(`${activeTabIndex}TASK_CHECK_${t.id}`);
            if (el) el.checked = false;
          });
        }, 10);
      }
    } else if (id === "grouptask") {
      if (!this.state.selectedTasks.length) {
        this.props.setMessage({ status: false, message: this.props.defConfig[this.props.defLang].notaskselected });
        return;
      }
      this.setState({ loading: true });
      const records = this.state.selectedTasks.map((id) => ({ id }));
      const resp = await changeTasks(records, "group");
      this.props.setMessage(resp);
      if (resp.status) this.refreshDataLocal(args);
      else this.setState({ loading: false, selectedTasks: [] });
    } else if (id === "ungrouptasks") {
      if (!args.item.ids?.length) return;
      this.setState({ loading: true });
      const records = args.item.ids.map((id) => (id?.id ? id : { id }));
      const resp = await changeTasks(records, "ungroup");
      this.props.setMessage(resp);
      if (resp.status) this.refreshDataLocal(args);
      else this.setState({ loading: false, selectedTasks: [] });
    }
  };

  /* ── main action dispatcher ──────────────────────────────── */
  setAction = async (args, fortemporalyIds) => {
    const mySeq = ++this.actionSeq;
    this.setState({ formDialogStatus: false });
    let data = [];
    try {
      if (fortemporalyIds) {
        data = fortemporalyIds;
      } else {
        data = args.element.parentNode.parentNode.id.replace("-popup", "").split("_");
      }
    } catch (ex) { /* noop */ }

    let id;
    let searchField = "id";
    if (data[data.length - 1] === "0" || data.length === 2) {
      id = data[0];
    } else {
      id = data[data.length - 1];
      searchField = "parentid";
    }

    const { value } = args.item;

    if (value === "UNGROUP") {
      const pos = find(this.state.data, id, searchField);
      const tasks = this.state.data[pos].subtasks;
      this.setState({ loading: true });
      const resp = await changeTasks(tasks, "ungroup");
      this.props.setMessage(resp);
      if (resp.status) this.refreshDataLocal(args);
      else this.setState({ loading: false, selectedTasks: [] });

    } else if (value === "PROGRESS") {
      const progress = findValueById(this.state.data, id, searchField, "progress");
      this.setState({ showProgressDialog: true, progressValue: progress || 0, id });

    } else if (value === "COMMENT") {
      const existingCommentary = findValueById(this.state.data, id, searchField, "commentary") || "";
      this.setState({ showCommentDialog: true, id, commentarySearchField: searchField, commentaryHistory: existingCommentary });

    } else if (value === "REASSIGN") {
      // Limpiar loading previo para no quedar con pantalla bloqueada.
      this.setState({ loading: false, showReassignDialog: true, id, reassignLoading: true, reassignCandidates: [], reassignTargetId: "" });
      try {
        const resp = await getReassignCandidates(id);
        if (mySeq !== this.actionSeq) return; // acción más nueva ya tomó la pantalla
        this.setState({
          reassignLoading: false,
          reassignCandidates: resp.status ? (resp.result || []) : [],
        });
        if (!resp.status) this.props.setMessage(resp);
      } catch (err) {
        if (mySeq !== this.actionSeq) return;
        this.setState({ reassignLoading: false, reassignCandidates: [] });
      }

    } else if (value === "FORCE") {
      // Solo trae las opciones de salida de la tarea (sin cargar el form completo).
      const taskName = findValueById(this.state.data, id, searchField, "taskname");
      this.setState({
        loading: false, showForceDialog: true, id,
        forceTaskName: taskName !== "NOT FOUND" ? taskName : "",
        forceLoading: true, forceOptions: [], forceComment: "",
      });
      try {
        const resp = await getTaskDetails(id, false);
        if (mySeq !== this.actionSeq) return;
        this.setState({
          forceLoading: false,
          forceOptions: resp.status ? (resp.options || []) : [],
        });
        if (!resp.status) this.props.setMessage(resp);
      } catch (err) {
        if (mySeq !== this.actionSeq) return;
        this.setState({ forceLoading: false, forceOptions: [] });
      }

    } else if (value === "SUSPEND") {
      // Pide motivo obligatorio antes de suspender (ver confirmSuspend / modal).
      this.setState({ loading: false, showSuspendDialog: true, id, suspendReason: "", suspendBusy: false });

    } else if (value === "RESUME") {
      this.setState({ loading: true });
      const resp = await resumeTask(id);
      this.props.setMessage({ status: !!resp.status, message: resp.message_es || resp.message || (resp.status ? this.props.defConfig[this.props.defLang].taskresumedlabel || "Tarea reanudada" : this.props.defConfig[this.props.defLang].taskresumeerrorlabel || "No se pudo reanudar") });
      if (resp.status) { this.startSkeleton(); this.props.refreshData?.(); this.props.onSuspendResume?.('resume'); }
      else this.setState({ loading: false });

    } else if (value === "TAKE") {
      const pos = find(this.state.data, id, searchField);
      const task = this.state.data[pos];
      this.setState({ loading: true });
      const resp = await changeTasks([{ id: task.id }], "take");
      this.props.setMessage(resp);
      if (resp.status) this.refreshDataLocal(args);
      else this.setState({ loading: false });

    } else if (value === "RELEASE") {
      const pos = find(this.state.data, id, searchField);
      const task = this.state.data[pos];
      this.setState({ loading: true });
      const resp = await changeTasks([{ id: task.id }], "release");
      this.props.setMessage(resp);
      if (resp.status) this.refreshDataLocal(args);
      else this.setState({ loading: false });

    } else if (value === "RESOLVE" || value === "VIEW") {
      this.setState({ loading: true, flowData: [], formDataTask: [] });
      const response = await preparTaskForm(id, searchField, this.state.data, this.state.vars, this.props.header, fortemporalyIds);
      if (mySeq !== this.actionSeq) return; // acción más nueva (ej: REASSIGN) ya tomó la pantalla
      if (response.task) {
        setTimeout(() => {
          if (mySeq !== this.actionSeq) return;
          this.setState({
            flowData:        response.flowData,
            taskActions:     value === "VIEW" ? [] : response.task.options,
            formDataTask:    response.formData,
            datapostload:    response.datapostload,
            datasubtitle:    response.datasubtitle,
            datatitle:       response.datatitle,
            taskHeader:      response.taskHeader,
            taskName:        response.taskName,
            isParent:        response.isParent,
            id:              response.taskId,
            commentary:      response.commentary,
            wfpflowid:       response.wfpflowid,
            wfptaskid:       response.wfptaskid,
            officecode:      response.officecode,
            readOnly:        value === "VIEW",
            formDialogStatus: true,
            loading:         false,
          });
        }, 500);
      } else {
        this.setState({ formDataTask: [], formDialogStatus: false, loading: false });
      }
    }
  };

  /* ── data refresh ────────────────────────────────────────── */
  refreshDataLocal = async (args) => {
    this.setState({ skeletonLoading: true, formDialogStatus: false, expandedIds: new Set(), selectMode: false, selectedTasks: [], selAll: false });
    const resp = await prepareGetTasks({ taskId: this.props.taskId, flowId: this.props.flowId, statusId: this.props.statusId });
    const nextRows = typeof this.props.filterRows === "function"
      ? this.props.filterRows(resp.activetasks || [])
      : resp.activetasks || [];
    this.setState({ skeletonLoading: false, loading: false, data: nextRows, vars: resp.flowvars || [] });
  };

  refreshData = async (args) => {
    this.setState({ id: null });
    // Una sola carga: el padre dispara el fetch y este grid se re-sincroniza
    // vía dataSource (componentDidUpdate). Evita duplicar el request (antes:
    // refreshDataLocal + props.refreshData = 2 llamadas). Si el padre no
    // provee callback, se refresca localmente como fallback.
    if (typeof this.props.refreshData === "function") {
      this.props.refreshData();
      return;
    }
    await this.refreshDataLocal(args);
  };

  formDialogClose = () => {
    // Cerrar el modal NO modifica datos → no se recarga la tabla ni se muestra
    // spinner. (El refresh real solo ocurre al ejecutar/completar una tarea o
    // ante TAREA_COMPLETADA del iframe.) Cierre instantáneo.
    this.setState({ formDialogStatus: false, expandedIds: new Set() });
  };

  clearTaskId = async () => this.setState({ id: null });

  onChangeForm = async (wfpflowid, wfptaskid, name, value) => {
    const pos = this.state.vars.findIndex(
      (k) => k.WFPFLOWID === wfpflowid && k.VARNAME === name
    );
    if (pos >= 0) this.state.vars[pos]["VARVALUE"] = value;
  };

  getFormInfo = async (formcode) => {
    const { formDialogs } = this.state;
    const idx = formDialogs.findIndex((i) => i.id === formcode);
    if (idx >= 0) return formDialogs[idx].resp;
    const resp = await getTemplateForm(formcode);
    if (resp.status) formDialogs.push({ id: formcode, resp });
    return resp;
  };

  /* ── simple modals ───────────────────────────────────────── */
  saveProgress = () => this.setState({ showProgressDialog: false });

  confirmReassign = async () => {
    const { id, reassignTargetId } = this.state;
    if (!reassignTargetId) {
      this.props.setMessage({ status: false, message: this.props.defConfig[this.props.defLang].selecttargetuserlabel || "Selecciona un usuario destino" });
      return;
    }
    this.setState({ reassignBusy: true });
    const resp = await reassignTask(id, reassignTargetId);
    this.props.setMessage({
      status: !!resp.status,
      message: resp.message_es || resp.message || (resp.status ? this.props.defConfig[this.props.defLang].taskreassignedlabel || "Tarea reasignada" : this.props.defConfig[this.props.defLang].taskreassignerrorlabel || "No se pudo reasignar"),
    });
    this.setState({ reassignBusy: false });
    if (resp.status) {
      this.setState({ showReassignDialog: false, reassignTargetId: "" });
      this.refreshDataLocal();
      this.props.refreshData?.();
    }
  };

  // Ejecuta la tarea saltando el formulario: el usuario solo elige la opción
  // de salida (mismo listado que vería en el form) y un comentario opcional.
  // Reusa el mismo flujo redux (ProcessActions.executeTask) que usa
  // DialogExecuteTask al enviar el form, así se conservan el toast, el
  // loader global y el refresco automático de la bandeja.
  confirmForce = (optionValue) => {
    const { id, forceComment, forceTaskName } = this.state;
    const commentVal = (forceComment || "").trim();
    if (!commentVal) return; // motivo obligatorio — no debería llegar acá si el botón está disabled
    const params = {};
    if (commentVal && forceTaskName) {
      const date = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const formattedDate = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
      params["comment_" + forceTaskName] = formattedDate + ":" + commentVal + "\n";
    }
    this.setState({ showForceDialog: false, forceComment: "" });
    this.props.forceExecuteTask?.({ id, isParent: false, params, optionValue });
  };

  // Suspende la tarea con motivo obligatorio (backend concatena el motivo en
  // ADDCOMMENTARY como "SUSPENDIDA por USER el FECHA (motivo)").
  confirmSuspend = async () => {
    const { id, suspendReason } = this.state;
    const reason = (suspendReason || "").trim();
    if (!reason) return;
    this.setState({ suspendBusy: true });
    const resp = await suspendTask(id, reason);
    this.setState({ showSuspendDialog: false, suspendBusy: false, suspendReason: "" });
    this.props.setMessage({ status: !!resp.status, message: resp.message_es || resp.message || (resp.status ? this.props.defConfig[this.props.defLang].tasksuspendedlabel || "Tarea suspendida" : this.props.defConfig[this.props.defLang].tasksuspenderrorlabel || "No se pudo suspender") });
    if (resp.status) { this.startSkeleton(); this.props.refreshData?.(); this.props.onSuspendResume?.('suspend'); }
  };

  saveCommentaryModal = async () => {
    const rawText = (this.commentRef.current?.value || "").trim();
    if (!rawText) return;
    const username = getUserName() || "usuario";
    const sysdate = new Date().toISOString();
    // Backend does CONCAT(existing, '|_|', ?) — only send the new formatted entry
    const newEntry = `${rawText} por ${username} el ${sysdate}`;
    const resp = await saveCommentary({ id: this.state.id, commentary: newEntry });
    this.props.setMessage(resp);
    if (resp.status) {
      // Update local state instead of refetching: append the new entry to the
      // open history and to the row's `commentary` field (drives the count badge).
      const { id, commentarySearchField } = this.state;
      const searchField = commentarySearchField || "id";
      const updatedHistory = this.state.commentaryHistory
        ? `${this.state.commentaryHistory}|_|${newEntry}`
        : newEntry;
      if (this.commentRef.current) this.commentRef.current.value = "";
      this.setState((prev) => ({
        commentaryHistory: updatedHistory,
        data: prev.data.map((row) =>
          row[searchField] == id ? { ...row, commentary: updatedHistory } : row
        ),
      }));
    }
  };

  parseCommentaryEntries = (raw) => {
    if (!raw) return [];
    return raw.split('|_|').filter(s => s.trim()).map(s => s.trim());
  };

  onChange = (e) => this.setState({ keySearch: e.target.value.toLowerCase() });

  /* ── expand / collapse ───────────────────────────────────── */
  toggleExpand = (idKey) => {
    const expanded = new Set(this.state.expandedIds);
    if (expanded.has(idKey)) expanded.delete(idKey);
    else expanded.add(idKey);
    this.setState({ expandedIds: expanded });
  };

  toggleExpandAll = () => {
    const parents = new Set();
    this.state.data.forEach((r) => {
      if (r.subtasks?.length) parents.add(String(Array.isArray(r.id) ? r.id.join(",") : r.id));
    });
    const anyOpen = [...parents].some((k) => this.state.expandedIds.has(k));
    this.setState({ expandedIds: anyOpen ? new Set() : parents });
  };

  /* ── desktop: render rows recursivamente ────────────────── */
  renderTableRows(rows, depth = 0, startIdx = 0) {
    const out = [];
    (rows || []).forEach((row, idx) => {
      const idKey   = Array.isArray(row.id) ? row.id.join(",") : String(row.id);
      const params  = idKey.replaceAll(",", "_") + "_" + row.parentid;
      const isParent  = row.subtasks?.length > 0;
      const isExpanded = this.state.expandedIds.has(idKey);
      const isGrouped  = Array.isArray(row.id) && row.isgroupable === "YES";
      const isLocked   = row.locked;
      const canAct     = row.userowner <= 2;
      const { defConfig, defLang } = this.props;
      const takeLabel    = row.assigned
        ? defConfig[defLang].release
        : isLocked
          ? (defConfig[defLang].tasktaken || "Asignada")
          : defConfig[defLang].takelabel;
      const executeLabel = isLocked ? defConfig[defLang].view : defConfig[defLang].executelabel;
      const commentCount = row.commentary ? row.commentary.split("|_|").filter((s) => s.trim()).length : 0;

      out.push(
        <tr
          key={idKey + "_" + idx}
          className={
            "xft-tr" +
            (row.userowner == 1 ? " xft-tr-owned" : "") +
            (isLocked ? " xft-tr-locked" : "")
          }
        >
          {/* ── checkbox selección masiva ── */}
          {this.state.selectMode && (
            <td className="xft-td xft-td-check">
              {!isGrouped && !isLocked && (
                <input
                  type="checkbox"
                  className="task-check xft-desktop-check"
                  id={"dTASK_CHECK_" + idKey}
                  style={{ width: 17, height: 17, cursor: "pointer" }}
                />
              )}
            </td>
          )}
          {/* ── # fila ── */}
          <td className="xft-td xft-td-num">
            {depth === 0 ? (
              row.officecode ? (
                <OfficeNumCell
                  num={startIdx + idx + 1}
                  officecode={row.officecode}
                  officename={row.officename}
                  wfpflowid={row.wfpflowid}
                />
              ) : (
                startIdx + idx + 1
              )
            ) : ""}
          </td>
          {/* ── acciones ── */}
          <td className="xft-td xft-td-actions">
            <div className="xft-actions">
              {this.props.suspendedMode ? (
                <button type="button" className="xft-btn xft-btn-primary"
                  title="Reanudar: vuelve a pendientes"
                  onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "RESUME" } })}>
                  <Play size={14} />
                  <span>{defConfig[defLang].resumelabel || "Reanudar"}</span>
                </button>
              ) : (
                <>
                  {isGrouped && (
                    <button type="button" className="xft-btn xft-btn-ghost"
                      onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "UNGROUP" } })}>
                      <Layers size={14} />
                      <span>{defConfig[defLang].ungrouplabel}</span>
                    </button>
                  )}
                  <button type="button"
                    className={"xft-btn xft-btn-takeslot " + (isLocked ? "xft-btn-ghost" : row.assigned ? "xft-btn-release" : "xft-btn-take")}
                    disabled={isLocked || !canAct}
                    title={isLocked && row.takenBy ? `Tomada por: ${row.takenBy}` : undefined}
                    onClick={() => !(isLocked || !canAct) && this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: row.assigned ? "RELEASE" : "TAKE" } })}>
                    {isLocked ? <Lock size={14} /> : row.assigned ? <UserMinus size={14} /> : <UserPlus size={14} />}
                    <span>{takeLabel}</span>
                  </button>
                  {!process.env.REACT_APP_HIDE_ADDCOMMENTARY && (
                    <button type="button" className="xft-btn xft-btn-ghost xft-btn-comment"
                      onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "COMMENT" } })}>
                      <MessageSquare size={14} />
                      <span>{defConfig[defLang].addcomment}</span>
                      {commentCount > 0 && <span className="xft-comment-count-badge xft-comment-count-badge--float">{commentCount}</span>}
                    </button>
                  )}
                  <MoreActionsMenu
                    items={[
                      {
                        hidden: isGrouped,
                        icon: <UserCog size={14} />,
                        label: defConfig[defLang].reassignlabel || "Reasignar",
                        disabled: !this.canReassign,
                        title: this.canReassign ? "Reasignar a otro usuario" : "Solo supervisores/admin",
                        onClick: () => this.canReassign && this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "REASSIGN" } }),
                      },
                      {
                        hidden: isGrouped,
                        icon: <Pause size={14} />,
                        label: defConfig[defLang].suspendlabel || "Suspender",
                        disabled: !this.canReassign,
                        title: this.canReassign ? "Suspender: sale de pendientes hasta reanudar" : "Solo supervisores/admin",
                        onClick: () => this.canReassign && this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "SUSPEND" } }),
                      },
                      {
                        hidden: isGrouped,
                        icon: <Zap size={14} />,
                        label: defConfig[defLang].forcetasklabel || "Forzar tarea",
                        disabled: !this.isAdmin,
                        title: this.isAdmin ? "Ejecutar sin validar el formulario" : "Solo admin",
                        onClick: () => this.isAdmin && this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "FORCE" } }),
                      },
                    ]}
                  />
                  <button type="button" className="xft-btn xft-btn-primary xft-btn-executeslot"
                    onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: isLocked ? "VIEW" : "RESOLVE" } })}>
                    {isLocked ? <Eye size={14} /> : <Check size={14} />}
                    <span>{executeLabel}</span>
                  </button>
                </>
              )}
            </div>
          </td>

          {/* ── fecha ── */}
          <td className="xft-td xft-td-date">{safeCell(row.startDate)}</td>
          {this.props.showTransferStore && (
            <td className={"xft-td xft-transfer-cell" + (this.props.transferStoreRemarkable && row.transferStore ? " xft-td-highlight" : "")}>
              {safeTransferStore(row.transferStore)}
            </td>
          )}

          {/* ── columnas de datos ── */}
          {(this.props.header || []).map((h, i) => {
            if (i >= MAX_COL) return null;
            const val = this.resolveFieldValue(row, i);
            const highlight = h.REMARKABLE === "YES" && val;
            return (
              <td
                key={i}
                className={"xft-td" + (highlight ? " xft-td-highlight" : "")}
                style={i === 0 ? { paddingLeft: depth * 22 + 12 } : {}}
              >
                {i === 0 && isParent && (
                  <button type="button" className="xft-expand-btn"
                    onClick={() => this.toggleExpand(idKey)}>
                    {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                )}
                {this.props.showTaskInfo && i === 0 && (
                  <Link to={"/getTasks/" + row.taskid + "/" + row.flowid} className="xft-flow-link">
                    <span>Flow: <b>{row.flowname}</b></span>
                    <span>Task: <b>{row.taskname}</b></span>
                  </Link>
                )}
                {val || (i === 0 ? "-" : "")}
                {isParent && i === 0 && row.totalTasks && (
                  <span className="xft-badge">+{row.totalTasks}</span>
                )}
              </td>
            );
          })}
        </tr>
      );

      if (isParent && isExpanded) {
        out.push(...this.renderTableRows(row.subtasks, depth + 1));
      }
    });
    return out;
  }

  /* ── skeleton rows ──────────────────────────────────────── */
  renderSkeletonRows(colCount) {
    const widths = ["wide", "short", "mid", "wide", "mid"];
    return Array.from({ length: 6 }, (_, i) => (
      <tr key={"skel_" + i} className="xft-tr">
        {Array.from({ length: colCount }, (_, j) => (
          <td key={j} className="xft-td">
            <div className={"xft-skeleton-cell " + widths[(i + j) % widths.length]} />
          </td>
        ))}
      </tr>
    ));
  }

  /* ── desktop: vista completa ─────────────────────────────── */
  renderDesktop() {
    const { defConfig, defLang, header } = this.props;
    const { data, toolbarOptions, keySearch, expandedIds, selectMode, selectedTasks, selAll, skeletonLoading } = this.state;
    if (!data?.length && !skeletonLoading || !header?.length) return null;

    const customBtns = toolbarOptions.filter((t) => typeof t === "object");
    const hasParents  = data.some((r) => r.subtasks?.length > 0);
    const anyExpanded = hasParents && [...expandedIds].length > 0;
    const colCount    =
      header.length + 3 + (this.props.showTransferStore ? 1 : 0) + (selectMode ? 1 : 0); // +1 para columna #
    const startIdx    = this.props.serverPagination
      ? ((this.props.serverPagination.page || 1) - 1) * (this.props.serverPagination.pageSize || 30)
      : 0;

    const modeLabel = selectMode === "group"
      ? (defConfig[defLang].grouptasks || "Agrupar")
      : (defConfig[defLang].take       || "Tomar");

    const confirmLabel = selectMode === "group"
      ? ("Confirmar agrupación")
      : ("Confirmar toma");

    // filtro de búsqueda en desktop
    const filtered = keySearch.length >= 2
      ? data.filter((row) => {
          for (let i = 0; i < MAX_COL; i++) {
            if (row["field" + i] && String(row["field" + i]).toLowerCase().includes(keySearch))
              return true;
          }
          return false;
        })
      : data;

    return (
      <div className="xft-wrapper">
        {/* ── Banner modo selección ── */}
        {selectMode && (
          <div className="xft-select-banner">
            <div className="xft-select-banner-info">
              <CheckSquare2 size={16} />
              <span>Modo selección · <b>{modeLabel}</b> — {selectedTasks.length} seleccionada{selectedTasks.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="xft-select-banner-actions">
              <button className="btn-selall" onClick={this.toggleSelectAll}>
                {selAll ? "Deseleccionar todo" : "Seleccionar todo"}
              </button>
              <button className="btn-confirm" onClick={this.confirmSelection} disabled={selectedTasks.length === 0}
                style={{ opacity: selectedTasks.length === 0 ? 0.5 : 1 }}>
                {confirmLabel} ({selectedTasks.length})
              </button>
              <button className="btn-cancel" onClick={this.cancelSelection}>Cancelar</button>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="xft-toolbar">
          <div className="xft-toolbar-left">
            {customBtns.map((btn) => (
              <button
                key={btn.id}
                type="button"
                className={"xft-toolbar-btn" + (selectMode && btn.id === selectMode ? " xft-toolbar-btn-active" : "")}
                onClick={() => this.contextToolbarMenuTaskClick({ item: btn })}
                disabled={skeletonLoading}
              >
                {btn.prefixIcon && <span className={btn.prefixIcon}></span>}
                <span>{btn.text}</span>
              </button>
            ))}
            {hasParents && (
              <button type="button" className="xft-toolbar-btn" onClick={this.toggleExpandAll} disabled={skeletonLoading}>
                {anyExpanded ? <ChevronsUp size={14} /> : <ChevronsDown size={14} />}
                <span>{anyExpanded
                  ? (defConfig[defLang].collapseall || "Colapsar")
                  : (defConfig[defLang].expandall   || "Expandir")
                }</span>
              </button>
            )}
          </div>
          <div className="xft-toolbar-right">
            <div className="xft-search">
              <Search size={14} className="xft-search-icon" />
              <input
                type="text"
                className="xft-search-input"
                placeholder={defConfig[defLang].searchlabel || "Buscar..."}
                value={keySearch}
                onChange={this.onChange}
                name="xft-search-global"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore=""
                aria-autocomplete="none"
                readOnly={this.state.searchReadOnly}
                onFocus={() =>
                  this.state.searchReadOnly &&
                  this.setState({ searchReadOnly: false })
                }
                onKeyDown={(e) => e.key === "Enter" && this.triggerDeepSearch()}
              />
              {keySearch && (
                <button type="button" className="xft-search-clear" onClick={() => this.setState({ keySearch: "" })}>
                  <X size={13} />
                </button>
              )}
              <button
                type="button"
                className="xft-search-deep"
                title={defConfig[defLang].deepsearchlabel || "Búsqueda profunda: busca el texto como variable del flujo (≥2 caracteres)"}
                onClick={this.triggerDeepSearch}
                disabled={this.state.deepSearching || keySearch.trim().length < 2}
              >
                {this.state.deepSearching
                  ? <Loader2 size={14} className="xft-search-deep-spin" />
                  : <ZoomIn size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="xft-scroll">
          <table className="xft-table">
            <thead className="xft-thead">
              <tr>
                {selectMode && <th className="xft-th xft-th-check"></th>}
                <th className="xft-th xft-th-num">#</th>
                <th className="xft-th xft-th-actions">{defConfig[defLang].actionslabel}</th>
                <th className="xft-th xft-th-date">{defConfig[defLang].startdatelabel}</th>
                {this.props.showTransferStore && (
                  <th className={"xft-th xft-transfer-cell" + (this.props.transferStoreRemarkable ? " xft-td-highlight" : "")}>{this.props.transferStoreLabel || "TIENDAS TRASPASO"}</th>
                )}
                {header.map((h, i) => (
                  <th key={i} className="xft-th">{h.VARLABEL}</th>
                ))}
              </tr>
            </thead>
            <tbody className="xft-tbody">
              {skeletonLoading
                ? this.renderSkeletonRows(colCount)
                : filtered.length > 0
                  ? this.renderTableRows(filtered, 0, startIdx)
                  : (
                    <tr>
                      <td className="xft-td-empty" colSpan={colCount}>
                        {keySearch ? "Sin resultados para \"" + keySearch + "\"" : defConfig[defLang].nopendings || "Sin tareas"}
                      </td>
                    </tr>
                  )
              }
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ── mobile: card de tarea ───────────────────────────────── */
  renderMobileCard(data, params, checkboxPrefix, rowNum = null) {
    const { defConfig, defLang, header, showTaskInfo, noToolBar } = this.props;
    const { notGroupable } = this.state;
    const isLocked = data.locked;
    const cfg = defConfig?.[defLang] || {};
    const commentCount = data.commentary ? data.commentary.split("|_|").filter((s) => s.trim()).length : 0;

    const BOOL_YES = /^(si|sí|yes|1|true)$/i;

    const fields = (header || []).reduce((acc, b, index) => {
      if (index >= MAX_COL) return acc;
      const rawVal = data["field" + index];
      const val = this.resolveFieldValue(data, index);
      if (!val && index > 0) return acc;
      const isArrayActive = Array.isArray(rawVal) && rawVal[0];
      const isBoolActive = !Array.isArray(rawVal) && BOOL_YES.test(String(val).trim());
      acc.push({
        b,
        index,
        priority: b.REMARKABLE === "YES",
        boolActive: isArrayActive || isBoolActive,
        val,
      });
      return acc;
    }, []);

    const primary = fields[0];
    const rest = fields.slice(1);

    return (
      <div className="xftc-card" key={params}>

        {/* Cabecera: flujo·tarea + número + checkbox */}
        <div className="xftc-header">
          {showTaskInfo && data.flowname ? (
            <Link to={"/getTasks/" + data.taskid + "/" + data.flowid} className="xftc-badge">
              <span>{data.flowname}</span>
              <span className="xftc-badge-sep">·</span>
              <span>{data.taskname}</span>
            </Link>
          ) : <span />}
          <div className="xftc-header-right">
            {rowNum !== null && (
              <span className="xftc-row-num-wrap">
                <span className="xftc-row-num">#{rowNum}</span>
                {data.officecode && (
                  <span className="xftc-section-chip" title={"Sección: " + data.officecode + (data.officename ? " · " + data.officename : "") + (data.wfpflowid && data.wfpflowid > 0 ? " | Flujo #" + data.wfpflowid : "")}>
                    <span className="xftc-section-code">{data.officecode}</span>
                    {data.officename && <span className="xftc-section-name">{data.officename}</span>}
                    {data.wfpflowid && data.wfpflowid > 0 && <span className="xftc-section-flow">#{data.wfpflowid}</span>}
                  </span>
                )}
              </span>
            )}
            {!isLocked && !notGroupable && !noToolBar && !Array.isArray(data.id) && (
              <label className="xft-mobile-check-label" htmlFor={checkboxPrefix + "TASK_CHECK_" + data.id}>
                <input
                  className="task-check xflow-mobile-task-check"
                  id={checkboxPrefix + "TASK_CHECK_" + data.id}
                  type="checkbox"
                />
              </label>
            )}
            {Array.isArray(data.id) && data.isgroupable === "YES" && (
              <button type="button" className="xft-card-shortcut-btn"
                onClick={() => this.contextMenuClick({ item: { id: "ungrouptasks", ids: data.id } })}>
                <span className="e-btn-icon e-icons e-double-resize-1 e-custom-icon-group info-color"></span>
              </button>
            )}
          </div>
        </div>

        {/* Campo principal (order / ID) */}
        {primary && (
          <div className={
            "xftc-title-block" +
            (primary.boolActive ? " xftc-title-block--bool" :
              primary.priority && primary.val ? " xftc-title-block--highlight" : "")
          }>
            <span className="xftc-title-label">{primary.b.VARLABEL}</span>
            <div className="xftc-title-row">
              <span className="xftc-title">{primary.val || "—"}</span>
              {data.totalTasks && primary.val && (
                <span className="xftc-tasks-badge">+{data.totalTasks}</span>
              )}
            </div>
          </div>
        )}

        {/* Tiendas traspaso (opcional) */}
        {this.props.showTransferStore && data.transferStore && (
          <div className="xftc-field xftc-field--full xftc-field--transfer">
            <span className="xftc-field-label">{this.props.transferStoreLabel || "TIENDAS TRASPASO"}</span>
            <span className="xftc-field-value">{safeTransferStore(data.transferStore)}</span>
          </div>
        )}

        {/* Campos adicionales en grilla 2 columnas */}
        {rest.length > 0 && (
          <div className="xftc-fields">
            {rest.map(({ b, index, priority, boolActive, val }) => {
              const cls = priority && val
                ? " xftc-field--highlight"
                : boolActive
                  ? " xftc-field--bool"
                  : "";
              return (
                <div key={index} className={"xftc-field" + cls}>
                  <span className="xftc-field-label">{b.VARLABEL}</span>
                  <span className="xftc-field-value">{val}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Botones de acción */}
        <div className="xftc-actions">
          {this.props.suspendedMode ? (
          <div className="xftc-actions-primary">
            <button type="button" className="xftc-btn xftc-btn--execute"
              onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "RESUME" } })}>
              <Play size={17} />
              <span>{defConfig[defLang].resumelabel || "Reanudar"}</span>
            </button>
          </div>
          ) : (<>
          {/* CTA principal — Ejecutar o Asignada */}
          <div className="xftc-actions-primary">
            {isLocked ? (
              <button type="button" className="xftc-btn xftc-btn--disabled" disabled>
                <Lock size={17} />
                <span>{data.takenBy ? `Asignada a ${data.takenBy}` : (cfg.tasktaken || "Asignada")}</span>
              </button>
            ) : (
              <button type="button" className="xftc-btn xftc-btn--execute"
                onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "RESOLVE" } })}>
                <Check size={17} />
                <span>{cfg.executelabel}</span>
              </button>
            )}
          </div>
          {/* Acciones secundarias */}
          <div className="xftc-actions-secondary">
            {isLocked ? (
              <button type="button" className="xftc-btn xftc-btn--disabled" disabled>
                <Lock size={14} />
                <span>{data.assigned ? cfg.release : cfg.tasktaken}</span>
              </button>
            ) : (
              <button type="button"
                className={"xftc-btn " + (data.assigned ? "xftc-btn--release" : "xftc-btn--take")}
                onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: data.assigned ? "RELEASE" : "TAKE" } })}>
                {data.assigned ? <UserMinus size={14} /> : <UserPlus size={14} />}
                <span>{data.assigned ? cfg.release : cfg.takelabel}</span>
              </button>
            )}
            {!process.env.REACT_APP_HIDE_ADDCOMMENTARY && (
              <button type="button" className="xftc-btn xftc-btn--comment"
                onClick={() => this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "COMMENT" } })}>
                <MessageSquare size={14} />
                <span>{cfg.comment}</span>
                {commentCount > 0 && <span className="xft-comment-count-badge xft-comment-count-badge--float">{commentCount}</span>}
              </button>
            )}
            <MoreActionsMenu
              triggerClassName="xftc-btn xftc-btn--more"
              items={[
                {
                  hidden: Array.isArray(data.id),
                  icon: <UserCog size={14} />,
                  label: defConfig[defLang].reassignlabel || "Reasignar",
                  disabled: !this.canReassign,
                  title: this.canReassign ? undefined : "Solo supervisores/admin",
                  onClick: () => this.canReassign && this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "REASSIGN" } }),
                },
                {
                  hidden: Array.isArray(data.id),
                  icon: <Pause size={14} />,
                  label: defConfig[defLang].suspendlabel || "Suspender",
                  disabled: !this.canReassign,
                  title: this.canReassign ? undefined : "Solo supervisores/admin",
                  onClick: () => this.canReassign && this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "SUSPEND" } }),
                },
                {
                  hidden: Array.isArray(data.id),
                  icon: <Zap size={14} />,
                  label: defConfig[defLang].forcetasklabel || "Forzar tarea",
                  disabled: !this.isAdmin,
                  title: this.isAdmin ? undefined : "Solo admin",
                  onClick: () => this.isAdmin && this.setAction({ element: { parentNode: { parentNode: { id: params } } }, item: { value: "FORCE" } }),
                },
              ]}
            />
          </div>
          </>)}
        </div>

        {/* Chips de contacto */}
        {!process.env.REACT_APP_HIDE_SHORTCUT && (data.havePhone || data.haveMail || data.haveCoords) && (
          <div className="xft-card-contact">
            {data.havePhone && (
              <a href={"tel:" + data.havePhone} className="xft-contact-chip xft-contact-chip--phone" title={"Llamar: " + data.havePhone}>
                <Phone size={13} />
                <span>{data.havePhone}</span>
              </a>
            )}
            {data.haveMail && (
              <a href={"mailto:" + data.haveMail} className="xft-contact-chip xft-contact-chip--mail" title={"Enviar correo: " + data.haveMail}>
                <Mail size={13} />
                <span>{data.haveMail}</span>
              </a>
            )}
            {data.haveCoords && (
              <button type="button" className="xft-contact-chip xft-contact-chip--map" title="Ver ubicación en el mapa"
                onClick={() => window.open("https://maps.google.com?q=" + data.haveCoords.latitude + "," + data.haveCoords.longitude)}>
                <MapPin size={13} />
                <span>Mapa</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  /* ── mobile: vista completa ──────────────────────────────── */
  renderMobile() {
    const { defConfig, defLang, noToolBar } = this.props;
    const { data, keySearch, activeTabIndex, selAll, notGroupable, compactMode, skeletonLoading } = this.state;
    if (!skeletonLoading && (!data?.length || !this.props.header?.length)) return null;
    if (!this.props.header?.length) return null;

    const tabs     = noToolBar ? [this.headerText[0]] : this.headerText;
    const tabData  = [this.getAssigned(), this.getUnassigned(), this.getAll()];
    const curIdx   = noToolBar ? 0 : activeTabIndex;
    const curData  = tabData[curIdx] || [];

    const toggleCompact = () => {
      const next = !compactMode;
      localStorage.setItem("xftc_compact", next ? "1" : "0");
      this.setState({ compactMode: next });
    };

    return (
      <div className={"xflow-mobile-tasks-view" + (compactMode ? " xftc-compact" : "")}>
        {/* Toolbar móvil */}
        <div className="xflow-mobile-task-toolbar">
          <div className="xft-toolbar">
            {!notGroupable && !noToolBar && (
              <div className="xft-toolbar-left">
                <button type="button" className="xft-toolbar-btn xft-toolbar-btn-primary"
                  onClick={() => this.contextMenuClick({ item: { id: "selectall" } })}>
                  {selAll ? defConfig[defLang].deselectall : defConfig[defLang].selectall}
                </button>
                <button type="button" className="xft-toolbar-btn xft-toolbar-btn-success"
                  onClick={() => this.contextMenuClick({ item: { id: "grouptask" } })}>
                  {defConfig[defLang].grouptasks}
                </button>
              </div>
            )}
            <div className="xft-toolbar-right" style={{ flex: 1 }}>
              <div className="xft-search" style={{ width: "100%" }}>
                <Search size={14} className="xft-search-icon" />
                <input
                  type="text"
                  className="xft-search-input"
                  placeholder="Search..."
                  value={keySearch}
                  onChange={this.onChange}
                  name="xft-search-global-m"
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore=""
                  aria-autocomplete="none"
                  readOnly={this.state.searchReadOnly}
                  onFocus={() =>
                    this.state.searchReadOnly &&
                    this.setState({ searchReadOnly: false })
                  }
                  onKeyDown={(e) => e.key === "Enter" && this.triggerDeepSearch()}
                />
                <button
                  type="button"
                  className="xft-search-deep"
                  title="Búsqueda profunda: busca el texto como variable del flujo (≥2 caracteres)"
                  onClick={this.triggerDeepSearch}
                  disabled={this.state.deepSearching || keySearch.trim().length < 2}
                >
                  {this.state.deepSearching
                    ? <Loader2 size={14} className="xft-search-deep-spin" />
                    : <ZoomIn size={14} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        {!noToolBar && (
          <div className="xft-tabs">
            {tabs.map((tab, i) => (
              <button key={tab.id} type="button"
                className={"xft-tab" + (activeTabIndex === i ? " xft-tab-active" : "")}
                onClick={() => this.setState({ activeTabIndex: i, formDialogStatus: false })}>
                {tab.text}
              </button>
            ))}
            <button
              type="button"
              className={"xftc-compact-btn xftc-compact-btn--tab" + (compactMode ? " xftc-compact-btn--active" : "")}
              onClick={toggleCompact}
              title={compactMode ? "Vista normal" : "Maximizar espacio"}
            >
              {compactMode ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
            </button>
          </div>
        )}

        {/* Cards */}
        <div className="xflow-mobile-task-layout">
          {skeletonLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="xftc-card xftc-card--skeleton">
                  <div className="xftc-skeleton-header" />
                  <div className="xftc-skeleton-title" />
                  <div className="xftc-skeleton-fields">
                    <div className="xftc-skeleton-field" />
                    <div className="xftc-skeleton-field" />
                    <div className="xftc-skeleton-field xftc-skeleton-field--long" />
                    <div className="xftc-skeleton-field" />
                  </div>
                  <div className="xftc-skeleton-actions" />
                </div>
              ))
            : curData.length > 0
              ? curData.map((d, mIdx) => {
                  const params = d.subtasks?.length > 0
                    ? d.id.toString().replace(",", "_") + "_" + d.parentid
                    : d.id + "_0";
                  return this.renderMobileCard(d, params, String(curIdx), mIdx + 1);
                })
              : (
                <div className="xft-no-data">
                  <CheckSquare2 size={32} />
                  <p>{defConfig[defLang].nopendings || "Sin tareas pendientes"}</p>
                </div>
              )
          }
        </div>
      </div>
    );
  }

  /* ── render principal ────────────────────────────────────── */
  render() {
    const { defConfig, defLang } = this.props;
    const {
      loading, isMobile, skeletonLoading,
      showProgressDialog, showCommentDialog, progressValue, commentaryHistory,
    } = this.state;

    return (
      <>
        <div className="control-pane" style={{ padding: isMobile ? "0" : "10px 15px" }}>
          {loading && !skeletonLoading && <Loading contained={true} />}

          <div className="control-section">

            {/* ── Modal progreso ── */}
            {showProgressDialog && (
              <div className="xft-modal-overlay" onClick={() => this.setState({ showProgressDialog: false })}>
                <div className="xft-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="xft-modal-header">
                    <span>Set Progress</span>
                    <button type="button" className="xft-modal-close"
                      onClick={() => this.setState({ showProgressDialog: false })}>
                      <X size={15} />
                    </button>
                  </div>
                  <div className="xft-modal-body">
                    <input type="range" min="0" max="100" value={progressValue}
                      className="xft-range"
                      onChange={(e) => this.setState({ progressValue: parseInt(e.target.value) })} />
                    <span className="xft-range-val">{progressValue}%</span>
                  </div>
                  <div className="xft-modal-footer">
                    <button type="button" className="xft-btn xft-btn-primary" onClick={this.saveProgress}>
                      Guardar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Drawer comentarios (portal → escapa del contenedor con transform) ── */}
            {showCommentDialog && ReactDOM.createPortal((() => {
              const entries = this.parseCommentaryEntries(commentaryHistory);
              return (
                <>
                  <div className="xft-drawer-overlay" onClick={() => this.setState({ showCommentDialog: false })} />
                  <div className="xft-comment-drawer">
                    <div className="xft-drawer-header">
                      <span>{defConfig[defLang].addcomment || "Comentarios"}</span>
                      <button type="button" className="xft-modal-close"
                        onClick={() => this.setState({ showCommentDialog: false })}>
                        <X size={15} />
                      </button>
                    </div>
                    <div className="xft-comment-history">
                      {entries.length === 0 ? (
                        <p className="xft-comment-empty">Sin comentarios aún</p>
                      ) : (
                        entries.map((entry, i) => {
                          const sysMatch = entry.match(/^(SUSPENDIDA|REANUDADA|ASIGNADA|LIBERADA)\s+por\s+(\S+)\s+el\s+(.+)$/i);
                          if (sysMatch) {
                            const [, action, user, dateStr] = sysMatch;
                            let displayDate = dateStr;
                            try {
                              const d = new Date(dateStr);
                              displayDate = d.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                + ' ' + d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
                            } catch(e) {}
                            return (
                              <div key={i} className="xft-comment-entry xft-comment-entry--system">
                                <span className="xft-comment-entry-badge">{action}</span>
                                <span className="xft-comment-entry-meta">por {user} · {displayDate}</span>
                              </div>
                            );
                          }
                          return (
                            <div key={i} className="xft-comment-entry">
                              <p className="xft-comment-entry-text">{entry}</p>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="xft-comment-compose">
                      <textarea ref={this.commentRef} className="xft-textarea xft-textarea--compose"
                        placeholder={defConfig[defLang].aditionalcommentary || "Ingrese su comentario..."} />
                      <div className="xft-comment-compose-actions">
                        <button type="button" className="xft-btn xft-btn-ghost xft-comment-compose-btn"
                          onClick={() => this.setState({ showCommentDialog: false })}>
                          {defConfig[defLang].closelabel || "Cerrar"}
                        </button>
                        <button type="button" className="xft-btn xft-btn-primary xft-comment-compose-btn"
                          onClick={this.saveCommentaryModal}>
                          Guardar
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              );
            })(), document.body)}

            {/* ── Modal reasignar (delegar) ── */}
            {this.state.showReassignDialog && (
              <div className="xft-modal-overlay xft-reassign-overlay" onClick={() => this.setState({ showReassignDialog: false })}>
                <div className="xft-modal xft-modal--reassign" onClick={(e) => e.stopPropagation()}>
                  <div className="xft-modal-header">
                    <span>{defConfig[defLang].reassignlabel || "Reasignar tarea"}</span>
                    <button type="button" className="xft-modal-close"
                      onClick={() => this.setState({ showReassignDialog: false })}>
                      <X size={15} />
                    </button>
                  </div>
                  <div className="xft-reassign-body">
                    {this.state.reassignLoading ? (
                      <p className="xft-reassign-hint">Cargando usuarios…</p>
                    ) : (this.state.reassignCandidates || []).length === 0 ? (
                      <p className="xft-reassign-hint">No hay usuarios disponibles para esta tarea (actor/oficina).</p>
                    ) : (
                      <ul className="xft-reassign-list">
                        {(this.state.reassignCandidates || []).map((u) => {
                          const name = [u.FIRSTNAME, u.LASTNAME].filter(Boolean).join(" ").trim();
                          const display = name || u.USERNAME;
                          const initials = display.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
                          const selected = this.state.reassignTargetId === String(u.USERID);
                          return (
                            <li
                              key={u.USERID}
                              className={"xft-reassign-item" + (selected ? " xft-reassign-item--selected" : "")}
                              onClick={() => this.setState({ reassignTargetId: String(u.USERID) })}
                            >
                              <div className="xft-reassign-avatar">{initials}</div>
                              <div className="xft-reassign-info">
                                <span className="xft-reassign-name">{display}</span>
                                {name && <span className="xft-reassign-username">@{u.USERNAME}</span>}
                              </div>
                              {selected && <div className="xft-reassign-check">✓</div>}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <div className="xft-modal-footer xft-reassign-footer">
                    <button
                      type="button"
                      className="xft-btn xft-btn-primary"
                      style={{ width: "100%" }}
                      disabled={this.state.reassignBusy || !this.state.reassignTargetId}
                      onClick={this.confirmReassign}
                    >
                      {this.state.reassignBusy ? "Reasignando…" : defConfig[defLang].reassignlabel || "Reasignar"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Modal forzar tarea (ADMIN) — ejecuta sin pasar por el form ── */}
            {this.state.showForceDialog && (
              <div className="xft-modal-overlay xft-force-overlay" onClick={() => this.setState({ showForceDialog: false })}>
                <div className="xft-modal xft-modal--force" onClick={(e) => e.stopPropagation()}>
                  <div className="xft-modal-header">
                    <span>{defConfig[defLang].forcetasklabel || "Forzar tarea"}</span>
                    <button type="button" className="xft-modal-close"
                      onClick={() => this.setState({ showForceDialog: false })}>
                      <X size={15} />
                    </button>
                  </div>
                  <div className="xft-force-body">
                    <p className="xft-force-hint">
                      {defConfig[defLang].forcetaskhint || "Se ejecutará la tarea sin pasar por el formulario ni sus validaciones. Indica el motivo y luego elige la opción de salida:"}
                    </p>
                    <textarea
                      autoFocus
                      className="xft-textarea xft-force-comment"
                      placeholder={defConfig[defLang].forcecommentplaceholder || "Motivo / observación (obligatorio)"}
                      value={this.state.forceComment}
                      onChange={(e) => this.setState({ forceComment: e.target.value })}
                    />
                    {this.state.forceLoading ? (
                      <p className="xft-force-hint">Cargando opciones…</p>
                    ) : (this.state.forceOptions || []).length === 0 ? (
                      <p className="xft-force-hint">No hay opciones de salida disponibles para esta tarea.</p>
                    ) : (
                      <div className="xft-force-options">
                        {this.state.forceOptions.map((opt, i) => (
                          <button key={i} type="button" className="xft-force-option-btn"
                            disabled={!this.state.forceComment.trim()}
                            title={!this.state.forceComment.trim() ? "Completa el motivo/observación antes de continuar" : undefined}
                            onClick={() => this.state.forceComment.trim() && this.confirmForce(opt)}>
                            <Zap size={14} />
                            {/* NO___OPTION = la tarea no tiene transicion de
                                salida en el diseno. Acá sí se ejecuta (cierra
                                la tarea), pero no dispara ninguna sucesora —
                                por eso no se rotula "Aceptar", que se leía
                                como "avanzar el flujo". */}
                            <span>{opt === "NO___OPTION" ? (defConfig[defLang].closetasknooptionlabel || "Cerrar tarea (sin salida)") : opt}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Modal suspender tarea — motivo obligatorio ── */}
            {this.state.showSuspendDialog && (
              <div className="xft-modal-overlay xft-suspend-overlay" onClick={() => !this.state.suspendBusy && this.setState({ showSuspendDialog: false })}>
                <div className="xft-modal xft-modal--suspend" onClick={(e) => e.stopPropagation()}>
                  <div className="xft-modal-header">
                    <span>{defConfig[defLang].suspendlabel || "Suspender"}</span>
                    <button type="button" className="xft-modal-close"
                      onClick={() => this.setState({ showSuspendDialog: false })}>
                      <X size={15} />
                    </button>
                  </div>
                  <div className="xft-force-body">
                    <p className="xft-force-hint">
                      {defConfig[defLang].confirmsuspendtasklabel || "La tarea saldrá de pendientes hasta que se reanude. Indica el motivo:"}
                    </p>
                    <textarea
                      autoFocus
                      className="xft-textarea xft-force-comment"
                      placeholder={defConfig[defLang].suspendreasonplaceholder || "Motivo de la suspensión (obligatorio)"}
                      value={this.state.suspendReason}
                      onChange={(e) => this.setState({ suspendReason: e.target.value })}
                    />
                  </div>
                  <div className="xft-modal-footer xft-reassign-footer">
                    <button
                      type="button"
                      className="xft-btn xft-btn-primary"
                      style={{ width: "100%" }}
                      disabled={this.state.suspendBusy || !this.state.suspendReason.trim()}
                      onClick={this.confirmSuspend}
                    >
                      {this.state.suspendBusy ? "Suspendiendo…" : defConfig[defLang].suspendlabel || "Suspender"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Dialog tarea (mantiene Syncfusion — no es el cuello de botella) ── */}
            <DialogExecuteTask
              id={this.state.id}
              clearTaskId={this.clearTaskId}
              commentary={this.state.commentary}
              flowId={this.props.flowId}
              taskId={this.props.taskId}
              varSchema={this.props.header}
              wfpflowid={this.state.wfpflowid}
              officecode={this.state.officecode}
              loading={loading}
              refreshData={this.refreshData}
              onChangeForm={this.onChangeForm}
              wfptaskid={this.state.wfptaskid}
              isParent={this.state.isParent}
              status={this.state.formDialogStatus}
              header={this.state.taskHeader}
              taskName={this.state.taskName}
              close={this.formDialogClose}
              flowData={this.state.flowData}
              data={this.state.formDataTask}
              datapostload={this.state.datapostload}
              actions={this.state.taskActions}
              animationSettings={{ effect: "SlideRight" }}
            />

            {(!loading || skeletonLoading) && (isMobile ? this.renderMobile() : this.renderDesktop())}
          </div>
        </div>
      </>
    );
  }
}

export default CompTreeGrid;
